import { lazy, ComponentType } from 'react';

/**
 * Drop-in replacement for React.lazy that survives deploys.
 *
 * Route targets are code-split, so their JS lives in hashed chunk files
 * (e.g. 1802.0a5f48a4.chunk.js). When a new build is deployed, Vercel replaces
 * those files with new hashes. A browser tab that was opened BEFORE the deploy
 * still holds the old index.html referencing the old hashes — so the next lazy
 * navigation requests a chunk that no longer exists. Vercel's SPA rewrite then
 * serves index.html (HTML) for the missing .js, the dynamic import throws
 * ("Unexpected token '<'" / ChunkLoadError), React unmounts the route, and the
 * user sees a blank screen. A hard refresh fixes it because it pulls the fresh
 * index.html + new chunk names.
 *
 * On the first such failure we do that refresh automatically. A sessionStorage
 * guard ensures we only force-reload once per session, so a genuinely broken
 * chunk (network down, real error) rethrows instead of reload-looping.
 */
const RELOAD_FLAG = 'chunk-reload-attempted';

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const component = await factory();
      // A clean load means we're on the current build — reset the guard so the
      // next deploy gets a fresh chance to reload.
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* sessionStorage unavailable — non-critical */
      }
      return component;
    } catch (error) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === 'true';
      } catch {
        /* sessionStorage unavailable — treat as not-yet-reloaded */
      }

      if (!alreadyReloaded) {
        try {
          window.sessionStorage.setItem(RELOAD_FLAG, 'true');
        } catch {
          /* ignore */
        }
        window.location.reload();
        // Never resolve, so the Suspense fallback stays up until the reload
        // navigates away — avoids flashing an error state before the refresh.
        return new Promise<{ default: T }>(() => {});
      }

      // We already reloaded once and it still failed — this is a real error.
      throw error;
    }
  });
}
